import { defineStore } from 'pinia'
import { ref } from 'vue'
import { $fetch } from 'ofetch'
import { getWidgetConfig } from '../runtime/widgetConfig'

export const useProjectStore = defineStore('project', () => {
    const projectCode = ref<string | null>(null)
    // In-flight guard: concurrent ensureProject() callers must share a single
    // POST /projects/start instead of each starting a new backend project.
    const inFlight = ref<Promise<string | null> | null>(null)

    function loadFromStorage() {
        if (typeof window === 'undefined') return
        projectCode.value = localStorage.getItem('pfe_projectCode')
    }

    function saveToStorage(code: string) {
        if (typeof window === 'undefined') return
        localStorage.setItem('pfe_projectCode', code)
    }

    async function ensureProject(preferredCode?: string | null) {
        if (typeof window === 'undefined') return null

        if (!projectCode.value) loadFromStorage()
        if (preferredCode && preferredCode !== projectCode.value) {
            projectCode.value = preferredCode
            saveToStorage(preferredCode)
        }
        if (projectCode.value) return projectCode.value

        // If a /projects/start request is already running, reuse it so we never
        // create duplicate backend projects.
        if (inFlight.value) return inFlight.value

        const { editorBaseUrl } = getWidgetConfig()
        const base = editorBaseUrl.endsWith('/') ? editorBaseUrl.slice(0, -1) : editorBaseUrl

        const promise = (async () => {
            const res: any = await $fetch(base + '/projects/start', {
                method: 'POST',
                body: { projectCode: preferredCode || undefined, meta: { source: 'frontend' } },
            })

            projectCode.value = res.projectCode
            if (projectCode.value) saveToStorage(projectCode.value)
            return projectCode.value
        })()

        inFlight.value = promise
        try {
            return await promise
        } finally {
            // Reset once settled (success or failure) so future calls can retry.
            inFlight.value = null
        }
    }

    return { projectCode, ensureProject, loadFromStorage }
})
